import type { IMeshInfo } from "@project/emerge-types";
import { createContext, useContext, useMemo } from "react";

const UserOptionsContext = createContext<Array<Record<string, unknown>> | null>(
  null,
);

export const MeshProvider = ({
  children,
  userOptions = [],
}: {
  children: React.ReactNode;
  userOptions?: Array<Record<string, unknown>>;
}) => {
  return (
    <UserOptionsContext.Provider value={userOptions}>
      {children}
    </UserOptionsContext.Provider>
  );
};

interface SubmeshLike {
  name?: string;
  url?: string;
  offset?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
  tags?: string[];
}

const extractMeshFromOption = (
  option: Record<string, unknown>,
  meshName: string,
): (IMeshInfo & { id: string }) | null => {
  const optionName = option.name as string | undefined;

  if (optionName === meshName && option.model) {
    return {
      id: meshName,
      name: meshName,
      url: option.model as string,
      tags: [option.kind as string],
      transform: {
        position: (option.offset as [number, number, number]) ?? [0, 0, 0],
        rotation: (option.rotation as [number, number, number]) ?? [0, 0, 0],
        scale: (option.scale as number) ?? 1,
      },
      deleted: false,
    };
  }

  const submeshKeys = ["meshTop", "meshBottom", "rollerA", "rollerB"];
  for (const key of submeshKeys) {
    const submesh = option[key] as SubmeshLike | undefined;
    if (submesh?.name === meshName && submesh.url) {
      return {
        id: meshName,
        name: meshName,
        url: submesh.url,
        tags: submesh.tags ?? [option.kind as string],
        transform: {
          position: submesh.offset ?? [0, 0, 0],
          rotation: submesh.rotation ?? [0, 0, 0],
          scale: submesh.scale ?? 1,
        },
        deleted: false,
      };
    }
  }

  const angleVariant = option.angleVariant as SubmeshLike | undefined;
  if (angleVariant?.name === meshName && angleVariant.url) {
    return {
      id: meshName,
      name: meshName,
      url: angleVariant.url,
      tags: angleVariant.tags ?? [option.kind as string, "angleVariant"],
      transform: {
        position: angleVariant.offset ?? [0, 0, 0],
        rotation: angleVariant.rotation ?? [0, 0, 0],
        scale: angleVariant.scale ?? 1,
      },
      deleted: false,
    };
  }

  return null;
};

export const useMesh = ({
  meshName,
}: {
  meshName: string;
}): (IMeshInfo & { id: string }) | null => {
  const userOptions = useContext(UserOptionsContext);

  return useMemo(() => {
    if (userOptions && userOptions.length > 0) {
      for (const option of userOptions) {
        const mesh = extractMeshFromOption(option, meshName);
        if (mesh) {
          return mesh;
        }
      }
    }

    console.error("useMesh: Mesh not found", meshName, {
      userOptionsLength: userOptions?.length ?? 0,
    });
    return null;
  }, [meshName, userOptions]);
};
